import { inject, Injectable, DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  AuthenticationDetails,
  ChallengeName,
  CognitoUser,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  from,
  Observable,
  Observer,
  switchMap,
  take,
} from "rxjs";
import {
  ConfirmPasswordEnum,
  ForgotPasswordEnum,
  ResetPasswordEnum,
  SignInEnum,
  SignUpEnum,
} from "../enums/authenticate.enum";
import {
  CognitoConfirmPasswordResult,
  CognitoForgotPasswordResult,
  CognitoResetPasswordResult,
  CognitoSignInResult,
  CognitoSignUpResult,
} from "../interfaces/auth.interfaces";
import { CognitoUtilsService } from "./cognito-utils.service";
import { LoggedUserService } from "../../core/services/logged-user.service";
import { LogoutEventService } from "./logout-event.service";

/**
 * Service responsible for managing user authentication with AWS Cognito.
 * Handles sign in, sign up, password management, and session validation.
 *
 * Subscribes to LogoutEventService to handle Cognito signOut when logout is triggered
 * from anywhere in the application (e.g., sidebar, error page).
 */
@Injectable({
  providedIn: "root",
})
export class AuthService {
  private readonly _cognitoUtilsService = inject(CognitoUtilsService);
  private readonly _loggedUserService = inject(LoggedUserService);
  private readonly _logoutEventService = inject(LogoutEventService);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _isAuthenticatedSubject = new BehaviorSubject(false);
  private readonly _isAuthenticated$ = this._isAuthenticatedSubject.pipe(
    distinctUntilChanged(),
  );
  private readonly _isLoadingSubject = new BehaviorSubject(false);
  private readonly _isLoading$ = this._isLoadingSubject.pipe(
    distinctUntilChanged(),
  );

  cognitoUser: CognitoUser | undefined;

  // Temporary storage for auto-login after OTP confirmation or force change password
  private _pendingCredentials: { email: string; password: string } | null =
    null;

  /**
   * Returns true if there are pending credentials stored (for force change password flow or trial registration)
   */
  get hasPendingCredentials(): boolean {
    return this._pendingCredentials !== null;
  }

  /**
   * Returns a copy of the pending credentials if available.
   * Used by AuthComponent to auto-login after trial registration.
   * Returns a copy to prevent external mutation of internal state.
   */
  get pendingCredentials(): { email: string; password: string } | null {
    return this._pendingCredentials ? { ...this._pendingCredentials } : null;
  }

  /**
   * Clears pending credentials
   */
  clearPendingCredentials(): void {
    this._pendingCredentials = null;
  }

  /**
   * Sets credentials and cognitoUser for trial registration flow.
   * Call this BEFORE signIn to enable auto-login after OTP confirmation.
   * The confirmSignUp method will use these credentials to auto-login.
   * @param email - User email
   * @param password - User password
   */
  setPendingCredentialsForTrial(email: string, password: string): void {
    this._pendingCredentials = { email, password };
    this.cognitoUser = new CognitoUser({
      Username: email,
      Pool: this._cognitoUtilsService.getUserPool(),
    });
  }

  constructor() {
    void this._checkUserSession();

    // Subscribe to logout events and handle Cognito signOut
    this._logoutEventService.onLogout$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._handleLogoutEvent());
  }

  /**
   * Handles logout event by signing out from Cognito and navigating to login.
   * Called reactively when LogoutEventService emits.
   */
  private _handleLogoutEvent(): void {
    const user = this._cognitoUtilsService.getCurrentUser();
    if (user) {
      user.signOut(() => this._redirectToLogin());
    } else {
      this._redirectToLogin();
    }
  }

  /**
   * Sends the user back to the login page via a full document navigation
   * (hard reload) rather than an in-app router navigation.
   *
   * A soft `router.navigate(["/"])` re-evaluates the async route guards while
   * the app is being torn down by the logout (erroring user$/tenant$ streams,
   * the delayed clearUser()), which can leave the user on a blank page until
   * they reload manually. A full document navigation guarantees a clean
   * bootstrap that lands on the login page — matching the behaviour of our
   * other apps and removing the need for a manual refresh.
   */
  private _redirectToLogin(): void {
    window.location.href = "/";
  }

  public signIn(
    email: string,
    password: string,
  ): Observable<CognitoSignInResult> {
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    this.cognitoUser = new CognitoUser({
      Username: email,
      Pool: this._cognitoUtilsService.getUserPool(),
    });

    return new Observable<CognitoSignInResult>(
      (observer: Observer<CognitoSignInResult>) => {
        this.cognitoUser?.authenticateUser(authenticationDetails, {
          onSuccess: (result: CognitoUserSession) => {
            this._onLoginSuccess(result, observer);
          },
          // First time login attempt - store credentials for force change password flow
          newPasswordRequired: () => {
            this._pendingCredentials = { email, password };
            observer.next({ type: SignInEnum.NEW_PASSWORD_REQUIRED });
            observer.complete();
          },
          totpRequired: (
            challengeName: ChallengeName,
            challengeParameters: Record<string, string>,
          ) => {
            observer.next({
              type: SignInEnum.TOTP_REQUIRED,
              result: {
                challengeName,
                challengeParameters,
              },
            });
            observer.complete();
          },
          onFailure: (error: Error) => {
            observer.error({ type: SignInEnum.FAIL, result: error });
          },
        });
      },
    );
  }

  forgotPassword(username: string): Observable<CognitoForgotPasswordResult> {
    const userData = {
      Username: username,
      Pool: this._cognitoUtilsService.getUserPool(),
    };

    const cognitoUser = new CognitoUser(userData);

    return new Observable<CognitoForgotPasswordResult>((observer) => {
      cognitoUser.forgotPassword({
        onSuccess: () => {
          observer.next({ type: ForgotPasswordEnum.SUCCESS });
          observer.complete();
        },
        inputVerificationCode: () => {
          observer.next({ type: ForgotPasswordEnum.INPUT_VERIFICATION_CODE });
          observer.complete();
        },
        onFailure: (error) => {
          observer.error({ type: ForgotPasswordEnum.FAIL, result: error });
        },
      });
    });
  }

  resetPassword(
    email: string,
    temporaryPassword: string,
    newPassword: string,
  ): Observable<CognitoResetPasswordResult> {
    return this.signIn(email, temporaryPassword).pipe(
      filter((event) => event.type === SignInEnum.NEW_PASSWORD_REQUIRED),
      switchMap(
        () =>
          new Observable<CognitoResetPasswordResult>((observer) => {
            this.cognitoUser?.completeNewPasswordChallenge(
              newPassword,
              undefined,
              {
                onSuccess: (session: CognitoUserSession) => {
                  void this._checkUserSession().then(() => {
                    observer.next({
                      type: ResetPasswordEnum.SUCCESS,
                      result: session,
                    });
                    observer.complete();
                  });
                },
                onFailure: (error: Error) => {
                  observer.error({ type: SignInEnum.FAIL, result: error });
                },
              },
            );
          }),
      ),
    );
  }

  /**
   * Resets password using stored credentials from the login attempt.
   * Used when user needs to change password on first login (force change password flow).
   * @param newPassword - The new password
   * @returns Observable that completes when password is reset
   */
  resetPasswordWithStoredCredentials(
    newPassword: string,
  ): Observable<CognitoResetPasswordResult> {
    if (!this._pendingCredentials) {
      return new Observable((observer) => {
        observer.error({
          type: ResetPasswordEnum.FAIL,
          result: new Error("No stored credentials available"),
        });
      });
    }

    const { email, password } = this._pendingCredentials;

    return this.resetPassword(email, password, newPassword).pipe(
      switchMap((result) => {
        this._pendingCredentials = null; // Clear credentials after successful reset
        return new Observable<CognitoResetPasswordResult>((observer) => {
          observer.next(result);
          observer.complete();
        });
      }),
    );
  }

  confirmNewPassword(
    email: string,
    verificationCode: string,
    password: string,
  ): Observable<CognitoConfirmPasswordResult> {
    const userData = {
      Username: email,
      Pool: this._cognitoUtilsService.getUserPool(),
    };

    const cognitoUser = new CognitoUser(userData);

    return new Observable<CognitoConfirmPasswordResult>((observer) => {
      cognitoUser.confirmPassword(verificationCode, password, {
        onSuccess: (result) => {
          observer.next({ type: ConfirmPasswordEnum.SUCCESS, result });
          observer.complete();
        },
        onFailure: (error: Error) => {
          observer.error({ type: ForgotPasswordEnum.FAIL, result: error });
        },
      });
    });
  }

  /**
   * Changes the password for the currently authenticated user
   * @param oldPassword - Current password
   * @param newPassword - New password
   * @returns Observable that completes when password is changed
   */
  changePassword(oldPassword: string, newPassword: string): Observable<void> {
    const user = this._cognitoUtilsService.getCurrentUser();

    if (!user) {
      return new Observable((observer) => {
        observer.error(new Error("No authenticated user"));
      });
    }

    return new Observable<void>((observer) => {
      user.getSession(
        (sessionError: Error | null, session: CognitoUserSession | null) => {
          if (sessionError || !session) {
            observer.error(sessionError || new Error("No valid session"));
            return;
          }

          user.changePassword(oldPassword, newPassword, (error) => {
            if (error) {
              observer.error(error);
            } else {
              observer.next();
              observer.complete();
            }
          });
        },
      );
    });
  }

  /**
   * Registers a new user with Cognito.
   * @param email - User email
   * @param password - User password
   * @returns Observable with signup result
   */
  signUp(email: string, password: string): Observable<CognitoSignUpResult> {
    // Store credentials for auto-login after OTP confirmation
    this._pendingCredentials = { email, password };

    // Set cognitoUser for confirmSignUp to use later
    this.cognitoUser = new CognitoUser({
      Username: email,
      Pool: this._cognitoUtilsService.getUserPool(),
    });

    return new Observable<CognitoSignUpResult>((observer) => {
      this._cognitoUtilsService
        .getUserPool()
        .signUp(email, password, [], [], (error) => {
          if (error) {
            this._pendingCredentials = null;
            this.cognitoUser = undefined;
            observer.error({ type: SignUpEnum.FAIL, result: error });
          } else {
            observer.next({ type: SignUpEnum.SUCCESS });
            observer.complete();
          }
        });
    });
  }

  /**
   * Confirms the user signup with the OTP code.
   * After confirmation, performs auto-login. LoggedUserService handles polling for user/tenant.
   * @param confirmationCode - The OTP code from email
   * @returns Observable that completes when user is logged in
   */
  confirmSignUp(confirmationCode: string): Observable<void> {
    // if the confirm sign up is called the cognito user must be defined
    return new Observable<void>((observer) => {
      this.cognitoUser?.confirmRegistration(confirmationCode, true, (error) => {
        if (error) {
          observer.error(error);
        } else {
          // Auto-login after successful OTP confirmation
          if (this._pendingCredentials) {
            const { email, password } = this._pendingCredentials;
            this._pendingCredentials = null; // Clear credentials

            // Enable trial polling in LoggedUserService BEFORE login
            // This way when MainLayout subscribes to user$/tenant$, it will retry
            this._loggedUserService.enableTrialPolling();

            this.signIn(email, password)
              .pipe(take(1))
              .subscribe({
                next: () => {
                  // Navigate immediately, LoggedUserService will handle polling
                  observer.next();
                  observer.complete();
                },
                error: (err) => observer.error(err),
              });
          } else {
            observer.next();
            observer.complete();
          }
        }
      });
    });
  }

  resendCode(): Observable<void> {
    // if the confirm sign up is called the cognito, user must be defined
    return new Observable<void>((observer) => {
      this.cognitoUser?.resendConfirmationCode((error) => {
        if (error) {
          observer.error(error);
        } else {
          observer.next();
          observer.complete();
        }
      });
    });
  }

  /**
   * Checks if the user is authenticated.
   * @returns an observable that emits the result of the authentication check
   */
  public isAuthenticated(): Observable<boolean> {
    // trigger the check manually and return the updated observable
    return from(this._checkUserSession()).pipe(
      switchMap(() => this._isAuthenticated$),
    );
  }

  public isLoading(): Observable<boolean> {
    return this._isLoading$;
  }

  /**
   * Handles the successful login.
   * @param session the user's session
   * @param observer the observer to notify
   */
  private _onLoginSuccess(
    session: CognitoUserSession,
    observer: Observer<CognitoSignInResult>,
  ): void {
    void this._checkUserSession().then(() => {
      observer.next({ type: SignInEnum.SUCCESS, result: session });
      observer.complete();
    });
  }

  /**
   * Checks the user's session.
   */
  private async _checkUserSession(): Promise<void> {
    this._isLoadingSubject.next(true);
    let isValid = false;
    try {
      const session = await this._cognitoUtilsService.getSession();
      isValid = session?.isValid() ?? false;
      if (isValid) {
        // A valid session (e.g. after login) clears the logout guard so a
        // future session expiry can trigger a fresh logout.
        this._logoutEventService.resetLogoutState();
      }
    } catch {
      // getSession() rejects when the refresh token is expired/invalid. Treat
      // it as "not authenticated" rather than letting the rejection propagate:
      // an errored auth guard aborts route matching entirely (no route matches)
      // and leaves the user stuck on a blank page.
      isValid = false;
    } finally {
      this._isAuthenticatedSubject.next(isValid);
      this._isLoadingSubject.next(false);
    }
  }
}
